import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Link, useHistory } from 'react-router-dom';
import { StyledAnimatedOnHover } from './SideTree';
import * as actions from '../../actions';
import { IStore } from '../../interfaces';
import { Localizer } from '../../utils/localizer';
import { Competitions } from '../../utils/competitions';
import { EnumSection, ITeam } from '../../interfaces/clientInterfaces';
import { useAppDispatch } from '../../utils/modules';
import { ISelectItem, Select } from './Select/Select';
import styles from './Header.module.css';

interface IStyledHeader {
	isMobileViewActive?: boolean;
}
export const StyledHeader = styled(StyledAnimatedOnHover)<IStyledHeader>`
	font-family: Europa-Big;
	font-size: ${props => props.isMobileViewActive ? '15px' : '18px'};
	text-align: center;
	margin: auto;
	cursor: pointer;
	text-align: left;
	padding: 5px 7px 5px 7px;
	user-select: none;
	color: white;
	a {
		text-decoration: none;
		color: white;
		text-align: center;
	}
	:hover {
		background-color: #F89504;
		a {
			color: black;
		}
		i {
			color:  #F89504;
		}
	}
`;


enum ISearchableType {
	Team = 'Team',
	Country = 'Country',
	Competition = 'Competition'
}

export default function Header() {

	const dispatch = useAppDispatch();

	const [showSearch, setShowSearch] = useState(false);
	
	const isMobileViewActive = useSelector(
		(completeState: IStore): boolean => {
			return !!completeState?.ui?.isMobileViewActive;
		});

	const showTreeMap = useSelector(
		(completeState: IStore): boolean => {
			return !!completeState?.ui?.showTreeMap;
		});

	const section = useSelector(
		(completeState: IStore): EnumSection => {
			return completeState?.ui?.section || EnumSection.home;
		});

	const teams = useSelector(
		(completeState: IStore): ITeam[] => {
			return completeState?.teams?.teams || [];
		});

	const clickTreeMapIcon = useCallback(
		() => {
			if (!showTreeMap) {
				dispatch(actions.showTreeMap());
			}
		},
		[dispatch, showTreeMap]
	);

	const searchableFields = useSelector(
		(completeState: IStore): {
			list: ISelectItem[];
			map: Map<string, ISearchableType>;
		} => {
			if (!completeState || !completeState.teams) {
				return {
					list: [],
					map: new Map<string, ISearchableType>()
				};
			}
			const map = new Map<string, ISearchableType>();

			const list: ISelectItem[] = [];
			for (const team of completeState.teams.teams) {
				const item: ISelectItem = {
					id: team.name,
					text: team.name,
					icon: [`./../../img/shield/${team.countryCode}_${team.teamCode}.png`],
				};
				map.set(item.id, ISearchableType.Team);
				list.push(item);
			}

			for (const countryCode of completeState.general.countryCodes) {
				const localizedName = Localizer.getLocalizedCountryName(countryCode);
				const item: ISelectItem = {
					id: localizedName,
					text: localizedName,
					icon: [`./../../img/flag/${countryCode}.png`],
				};
				map.set(item.id, ISearchableType.Country);
				list.push(item);
			}

			for (const competition of completeState.competitions.availableCompetitions) {
				const item: ISelectItem = {
					id: competition.id.toString(),
					text: competition.name,
					icon: [Competitions.getCompetitionRegionImagePath(competition.regionCode), Competitions.getRegionalTournamentImagePath(competition)],
				};
				map.set(item.id, ISearchableType.Competition);
				list.push(item);
			}

			return {
				list: list.sort((a,b) => a.text > b.text ? 1 : -1),
				map
			};
		});

	const history = useHistory();

	const search = useCallback(
		(value: string | null) => {
			setShowSearch(false);
			if (value) {
				const searchableFieldType = searchableFields.map.get(value);
				if (searchableFieldType) {
					switch (searchableFieldType) {
						case ISearchableType.Competition: {
							history.push(`/competitions?competition=${value}`);
							const competitionId = parseInt(value);
							dispatch(actions.addCompetition(competitionId));
							break;
						}
						case ISearchableType.Team: {
							const team = teams.find(team => team.name === value);
							if (team) {
								dispatch(actions.setHomeDetailTeam(team.teamId));
							}
							break;
						}
						case ISearchableType.Country: {
							history.push(`/${value}`);
							break;
						}
						default: {
							setShowSearch(false);
							break;
						}
					}
				}
			}
		},
		[searchableFields.map, history, teams, dispatch]
	);

	return (
		<>
			{showSearch && section === EnumSection.home &&
				<Select
					className={styles.searchSelect}
					selectedKey={''}
					items={searchableFields.list}
					onSelectionChange={search}
					searchable={true}
					selectValueIcon={'fa fa-search'}
					placeholder={'Click to search team, competition or country...'}
				/>				
			}
			<div className={styles.headerContainer}>
				<div className={styles.flexIconContainer}>
					{
						isMobileViewActive && !showTreeMap &&
							<div className={`${styles.rightHeader} hoverEffect`} onClick={clickTreeMapIcon}>
								<i className='fa fa-list'/>	
							</div>
					}
				</div>
				<div className={styles.flexCentralContainer}>
					<div>
						<div className={'header'}>
							<Link to={'/'}>
								{'Home'}
							</Link>
						</div>
						<div className={'header'}>
							<Link to={'/map'}>
								{'Map'}
							</Link>
						</div>
						<div className={'header'}>
							<Link to={'/competitions'}>
								{'Competitions'}
							</Link>
						</div>
						<div className={'header'}>
							<Link to={'/links'}>
								{'Links'}
							</Link>
						</div>
					</div>
				</div>
				{
					<Select
						className={styles.searchSelect}
						selectedKey={''}
						items={searchableFields.list}
						onSelectionChange={search}
						searchable={true}
						selectValueIcon={'fa fa-search'}
						placeholder={'Click to search team, competition or country...'}
						popoverFixSize={true}
					/>		
				}
			</div>
		</>
	);
}

